import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "../components/Reveal";
import { images } from "../data/content";
import { useSiteContent, resolveImage } from "../lib/cms";

function PageHero({ eyebrow, title, subtitle, image }) {
  return (
    <section className="relative flex min-h-[52vh] items-end">
      <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="hero-overlay absolute inset-0" />
      <div className="container-lodge relative z-10 pb-16 pt-28">
        <Reveal>
          <p className="eyebrow">{eyebrow}</p>
          <h1 className="display-h1 mt-4 max-w-3xl text-parchment">{title}</h1>
          {subtitle && <p className="mt-4 font-display text-2xl italic text-gold/90">{subtitle}</p>}
        </Reveal>
      </div>
    </section>
  );
}

export default function History() {
  const { block } = useSiteContent();
  const timeline = block("history_timeline").items || [];
  const notableFigures = block("history_figures").items || [];
  return (
    <div data-testid="history-page">
      <PageHero
        eyebrow="Our Story · 1845 – Present"
        title="From Armenian Street to Coleman Street."
        subtitle="One hundred and eighty years of unbroken fellowship."
        image={images.oldSingapore}
      />

      <section className="bg-paper-texture bg-parchment py-24">
        <div className="container-lodge">
          <div className="relative timeline-spine">
            {timeline.map((item, i) => (
              <Reveal key={`${item.year}-${i}`} delay={0.05}>
                <div
                  className={`relative mb-16 flex flex-col gap-6 md:mb-20 md:flex-row md:items-center ${
                    i % 2 === 1 ? "md:flex-row-reverse" : ""
                  }`}
                >
                  {/* node */}
                  <div className="absolute left-[7px] top-2 h-4 w-4 rounded-full border-2 border-gold bg-parchment md:left-1/2 md:-translate-x-1/2" />

                  {/* text */}
                  <div className={`pl-12 md:w-1/2 md:pl-0 ${i % 2 === 1 ? "md:pl-16" : "md:pr-16 md:text-right"}`}>
                    <span className="font-trajan text-3xl font-semibold text-gold">{item.year}</span>
                    <h3 className="mt-2 font-display text-3xl font-semibold text-sapphire-deep">{item.title}</h3>
                    <p className="mt-4 font-sans text-[16px] leading-[1.75] text-charcoal/80">{item.body}</p>
                  </div>

                  {/* image */}
                  <div className="pl-12 md:w-1/2 md:pl-0">
                    {item.image && (
                      <figure className={`${i % 2 === 1 ? "md:pr-16" : "md:pl-16"}`}>
                        <div className="overflow-hidden rounded-2xl border border-sapphire-deep/10 shadow-heritage">
                          <img src={resolveImage(item.image)} alt={item.caption || item.title} className="h-64 w-full object-cover" />
                        </div>
                        {item.caption && (
                          <figcaption className="mt-2 font-sans text-xs italic text-stonegrey">{item.caption}</figcaption>
                        )}
                      </figure>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NOTABLE FIGURES */}
      <section className="bg-sapphire-deep py-24 text-parchment">
        <div className="container-lodge">
          <Reveal>
            <p className="eyebrow">Figures of the Founding</p>
            <h2 className="display-h2 mt-3 max-w-2xl text-parchment">Men who shaped the Lodge and the settlement.</h2>
            <div className="gold-rule mt-6" />
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {notableFigures.map((f, i) => (
              <Reveal key={`${f.name}-${i}`} delay={i * 0.08}>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-7 backdrop-blur">
                  <h3 className="font-display text-2xl font-semibold text-gold">{f.name}</h3>
                  <p className="mt-2 font-sans text-[15px] leading-relaxed text-parchment/75">{f.note}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-3xl font-sans text-sm italic leading-relaxed text-parchment/55">
              Note: Sir Stamford Raffles was a Freemason initiated in Java under the Dutch constitution,
              not within Zetland itself. He is remembered here for his foundational role in the
              settlement the Lodge would come to serve.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-emerald-deep py-20 text-center text-parchment">
        <div className="container-lodge">
          <Reveal>
            <h2 className="display-h2 text-parchment">Building toward 2045.</h2>
            <p className="mx-auto mt-4 max-w-xl font-sans text-lg text-parchment/80">
              As we approach our bicentenary, we welcome a new generation to write the next chapter.
            </p>
            <Link to="/visit" className="btn-primary mt-8">
              Meet Our Community <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
